//
// # OaDestination
//
// The Depot / OpenAccess choice and the OpenAccess (lib, cell, view) fields,
// shared by Save As and Generate From Source. Each dialog keeps its own depot
// fields, which differ.
//
// ## The technology check
//
// Route vias resolve against the target library's technology, not the
// layout's PDK, so naming a library looks up its technology to warn before the
// save. A warning, not a gate: a library with no via definitions still accepts
// a layout with no routes.
//

import React, { useEffect, useState } from "react";

import { useWorkspace } from "../../context";
import type { OaTechInfo } from "../../types";
import { PathField, SEGMENT_FORMAT_ERROR } from "./PathField";
import { isValidSegment } from "./pathInput";

// ---------------------------------------------------------------------------
// Depot or OpenAccess
// ---------------------------------------------------------------------------

export interface DestinationChoiceProps {
  // Field label, e.g. "Save to" or "Create in".
  readonly label: string;
  // The radio group's `name`; distinct per dialog.
  readonly name: string;
  readonly toOpenAccess: boolean;
  readonly onChange: (toOpenAccess: boolean) => void;
}

export function DestinationChoice({
  label,
  name,
  toOpenAccess,
  onChange,
}: DestinationChoiceProps) {
  return (
    <div className="add-instance-param-field">
      <label className="add-instance-param-label">{label}</label>
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input
            type="radio"
            name={name}
            checked={!toOpenAccess}
            onChange={() => {
              onChange(false);
            }}
          />
          <span>Depot</span>
        </label>
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input
            type="radio"
            name={name}
            checked={toOpenAccess}
            onChange={() => {
              onChange(true);
            }}
          />
          <span>OpenAccess</span>
        </label>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// The (lib, cell, view) fields
// ---------------------------------------------------------------------------

// The OpenAccess address as the form holds it: untrimmed, as typed.
export interface OaAddress {
  readonly lib: string;
  readonly cell: string;
  readonly view: string;
}

// The touched-state keys these fields use, so a dialog's submit can mark them.
export const OA_ADDRESS_FIELDS = ["oaLib", "oaCell", "oaView"] as const;

export function oaAddressValid(address: OaAddress): boolean {
  return (
    isValidSegment(address.lib) &&
    isValidSegment(address.cell) &&
    isValidSegment(address.view)
  );
}

export interface OaAddressFieldsProps {
  readonly address: OaAddress;
  readonly onChange: (address: OaAddress) => void;
  readonly touched: Readonly<Record<string, boolean>>;
  readonly markTouched: (field: string) => void;
  // Helper text under the cell field, which each dialog words for its case.
  readonly cellHint: string;
}

// Library, cell, view: the order OpenAccess addresses a cellview in.
export function OaAddressFields({
  address,
  onChange,
  touched,
  markTouched,
  cellHint,
}: OaAddressFieldsProps) {
  const field = (
    key: (typeof OA_ADDRESS_FIELDS)[number],
    part: keyof OaAddress,
    label: string,
    placeholder: string,
    hint: string,
    autoFocus: boolean
  ) => {
    const valid = isValidSegment(address[part]);
    return (
      <PathField
        label={label}
        placeholder={placeholder}
        value={address[part]}
        onChange={(value) => {
          onChange({ ...address, [part]: value });
        }}
        onBlur={() => {
          markTouched(key);
        }}
        invalid={touched[key] && !valid}
        message={
          touched[key] && !valid
            ? { text: SEGMENT_FORMAT_ERROR, isError: true }
            : { text: hint, isError: false }
        }
        autoFocus={autoFocus}
      />
    );
  };
  return (
    <>
      {field(
        "oaLib",
        "lib",
        "Library",
        "GA_MY_BLOCK",
        "OpenAccess library. Created if it does not exist.",
        true
      )}
      {field("oaCell", "cell", "Cell", "opamp", cellHint, false)}
      {field(
        "oaView",
        "view",
        "View",
        "layout",
        "OpenAccess view name, e.g. layout.",
        false
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// The technology check
// ---------------------------------------------------------------------------

// What the technology check found. `unknown` means the gateway did not answer,
// which is distinct from a library with no technology (`known`, empty
// `techLib`).
export type TechCheck =
  | { readonly state: "idle" }
  | { readonly state: "checking" }
  | { readonly state: "unknown" }
  | { readonly state: "known"; readonly info: OaTechInfo };

// Look up the named library's technology while `enabled`. Debounced (each
// call is an OA open over an SSH tunnel) and cancelled on change so a stale
// answer cannot overwrite a newer one.
export function useOaTechCheck(enabled: boolean, lib: string): TechCheck {
  const workspace = useWorkspace();
  const [techCheck, setTechCheck] = useState<TechCheck>({ state: "idle" });
  const trimmed = lib.trim();
  const libValid = isValidSegment(trimmed);

  useEffect(() => {
    if (!enabled || !libValid) {
      setTechCheck({ state: "idle" });
      return;
    }
    let cancelled = false;
    setTechCheck({ state: "checking" });
    const timer = globalThis.setTimeout(() => {
      void workspace.oaTech(trimmed).then((result) => {
        if (cancelled) return;
        setTechCheck(
          result.ok
            ? { state: "known", info: result.val }
            : { state: "unknown" }
        );
      });
    }, 400);
    return () => {
      cancelled = true;
      globalThis.clearTimeout(timer);
    };
  }, [enabled, trimmed, libValid, workspace]);

  return techCheck;
}

// What the check means for a layout about to be written into the library.
// Renders nothing while idle or checking.
export function TechCheckNote({ check }: { readonly check: TechCheck }) {
  if (check.state === "known") {
    return <TechNote info={check.info} />;
  }
  if (check.state === "unknown") {
    return (
      <div
        className="add-instance-param-constraint"
        style={{ fontStyle: "normal" }}
      >
        Could not read this library&apos;s technology, so it has not been
        checked. That is usually the OpenAccess gateway being unreachable, not a
        problem with the library.
      </div>
    );
  }
  return null;
}

/**
 * Warns when the technology declares no via definitions: every route becomes an
 * `oaVia` naming one, so the library refuses any routed layout. Common for
 * libraries created to hold schematics, which keep the tool's default
 * technology. A library that does not exist yet fails the lookup instead, and
 * is created bound to this PDK's technology.
 */
function TechNote({ info }: { readonly info: OaTechInfo }) {
  const noVias = info.viaDefs.length === 0;
  if (!noVias) {
    return (
      <div
        className="add-instance-param-constraint"
        style={{ fontStyle: "normal" }}
      >
        Technology: {info.techLib} ({info.viaDefs.length} via definitions).
      </div>
    );
  }
  return (
    <span className="add-instance-param-error">
      {info.techLib === ""
        ? `Library "${info.lib}" resolves no technology at all, `
        : `Library "${info.lib}" uses technology "${info.techLib}", which declares no via definitions, `}
      so a layout with routes in it cannot be saved there. A library name that
      does not exist yet is created bound to this PDK&apos;s technology.
    </span>
  );
}
